import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle, faTimes, faCheckCircle, faSyncAlt,
  faPlus, faBan, faClock, faCookie, faKey
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';

const ProfileMergeConfirm = ({ duplicateInfo, isDarkMode, onClose, onResolved }) => {
  const [selectedAction, setSelectedAction] = useState('update');
  const [resolving, setResolving] = useState(false);

  const { existingProfile, existingProfileId, existingProfileName, newWorkflow, newWorkflowId, username, detectedAt } = duplicateInfo;

  const handleResolve = async () => {
    try {
      setResolving(true);

      const response = await axios.post('/auth-profiles/resolve-duplicate', {
        workflowId: newWorkflowId || newWorkflow?._id,
        action: selectedAction,
        profileId: existingProfileId || existingProfile?._id
      });

      if (response.data.success) {
        if (selectedAction === 'update') {
          toast.success('✓ Profile updated with fresh session!');
        } else if (selectedAction === 'create_new') {
          toast.success('✓ New profile created!');
        } else {
          toast.success('Duplicate discarded');
        }

        if (onResolved) {
          onResolved(response.data);
        }
        onClose();
      }
    } catch (error) {
      console.error('Failed to resolve duplicate:', error);
      toast.error('Failed to resolve duplicate');
    } finally {
      setResolving(false);
    }
  };

  const containerClass = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const borderClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
      <div className={`${containerClass} rounded-lg shadow-xl w-full max-w-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderClass} ${isDarkMode ? 'bg-yellow-900 bg-opacity-20' : 'bg-yellow-50'}`}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 text-xl" />
            <div>
              <h2 className="text-xl font-semibold">Duplicate Profile Detected</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                A login with username <span className="font-mono font-bold">"{username}"</span> was already captured
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Existing Profile */}
          <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faKey} className="text-blue-500" />
              Existing Profile
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Name:</span>
                <span className="ml-2 font-semibold">{existingProfileName || existingProfile?.name}</span>
              </div>
              <div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Created:</span>
                <span className="ml-2">
                  {existingProfile?.createdAt
                    ? new Date(existingProfile.createdAt).toLocaleString()
                    : 'Unknown'}
                </span>
              </div>
              <div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Last used:</span>
                <span className="ml-2">
                  {existingProfile?.lastUsed
                    ? new Date(existingProfile.lastUsed).toLocaleString()
                    : 'Never'}
                </span>
              </div>
              {existingProfile?.config?.cookies?.length > 0 && (
                <div>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Cookies:</span>
                  <span className="ml-2 flex items-center gap-1">
                    <FontAwesomeIcon icon={faCookie} className="text-orange-500 text-xs" />
                    {existingProfile.config.cookies.length} stored
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* New Login */}
          <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200'}`}>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faClock} className="text-green-500" />
              New Login
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Captured:</span>
                <span className="ml-2 font-semibold text-green-400">
                  {detectedAt ? new Date(detectedAt).toLocaleString() : 'Just now'}
                </span>
              </div>
              <div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Username:</span>
                <span className="ml-2 font-mono">{username}</span>
              </div>
              <div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Session:</span>
                <span className="ml-2 text-green-400">Fresh</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div>
            <h3 className="font-semibold mb-3">What would you like to do?</h3>
            <div className="space-y-3">
              {/* Update existing */}
              <label
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                  selectedAction === 'update'
                    ? isDarkMode
                      ? 'border-blue-500 bg-blue-900 bg-opacity-20'
                      : 'border-blue-500 bg-blue-50'
                    : isDarkMode
                    ? 'border-gray-700 hover:bg-gray-750'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="action"
                  value="update"
                  checked={selectedAction === 'update'}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  className="mt-1 w-4 h-4"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <FontAwesomeIcon icon={faSyncAlt} className="text-blue-500" />
                    Update existing profile with fresh session
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Replaces cookies/tokens with fresh ones from the new login. Keeps all other settings (role, name, configuration, etc.) unchanged.
                    <span className="font-semibold text-blue-400"> Recommended for session refresh.</span>
                  </p>
                </div>
              </label>

              {/* Create new */}
              <label
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                  selectedAction === 'create_new'
                    ? isDarkMode
                      ? 'border-green-500 bg-green-900 bg-opacity-20'
                      : 'border-green-500 bg-green-50'
                    : isDarkMode
                    ? 'border-gray-700 hover:bg-gray-750'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="action"
                  value="create_new"
                  checked={selectedAction === 'create_new'}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  className="mt-1 w-4 h-4"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <FontAwesomeIcon icon={faPlus} className="text-green-500" />
                    Create new profile
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Keeps both the existing profile and creates a new one with the fresh login.
                    <span className="font-semibold text-green-400"> Useful for maintaining multiple concurrent sessions.</span>
                  </p>
                </div>
              </label>

              {/* Discard */}
              <label
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                  selectedAction === 'discard'
                    ? isDarkMode
                      ? 'border-red-500 bg-red-900 bg-opacity-20'
                      : 'border-red-500 bg-red-50'
                    : isDarkMode
                    ? 'border-gray-700 hover:bg-gray-750'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="action"
                  value="discard"
                  checked={selectedAction === 'discard'}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  className="mt-1 w-4 h-4"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <FontAwesomeIcon icon={faBan} className="text-red-500" />
                    Discard new login
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Deletes the new workflow and keeps the existing profile unchanged.
                    <span className="font-semibold text-red-400"> Use if the new login was accidental.</span>
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-4 border-t ${borderClass}`}>
          <div className="text-sm text-gray-500">
            {selectedAction === 'update' && 'Existing profile will be updated'}
            {selectedAction === 'create_new' && 'Both profiles will be kept'}
            {selectedAction === 'discard' && 'New login will be discarded'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
            >
              Cancel
            </button>
            <button
              onClick={handleResolve}
              disabled={resolving}
              className={`px-4 py-2 rounded ${
                selectedAction === 'update'
                  ? isDarkMode
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-500 hover:bg-blue-600'
                  : selectedAction === 'create_new'
                  ? isDarkMode
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-green-500 hover:bg-green-600'
                  : isDarkMode
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-red-500 hover:bg-red-600'
              } text-white`}
            >
              {resolving ? (
                <>
                  <FontAwesomeIcon icon={faSyncAlt} spin className="mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Confirm
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileMergeConfirm;
